/**
 * PriceComparison - Preisvergleich-Komponente
 *
 * Zeigt alle Preise fuer ein Produkt von verschiedenen Lieferanten
 * mit Rueckverguetungen und Preisverlauf-Chart
 */

import { useState, useEffect } from 'react';
import {
  TrendingDown,
  TrendingUp,
  RefreshCw,
  ExternalLink,
  Check,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  LineChart,
  Euro,
  Percent,
  Package,
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import { Button, Spinner, Card } from '../common';

// Farben fuer Lieferanten im Chart
const SUPPLIER_COLORS = {
  Obeta: '#EF4444',       // Rot
  Sonepar: '#3B82F6',     // Blau
  UniElektro: '#10B981',  // Grün
  Zajadacz: '#EAB308',    // Gelb
  Default: '#6366F1',     // Indigo
};

const PriceComparison = ({ productId, productName, articleNumber, onClose }) => {
  const [prices, setPrices] = useState([]);
  const [priceHistory, setPriceHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [error, setError] = useState(null);

  // Preise laden
  const loadPrices = async () => {
    try {
      const response = await api.get(`/products/${productId}/prices`);
      setPrices(response.data.prices || []);
      setError(null);
    } catch (err) {
      console.error('Error loading prices:', err);
      setError('Preise konnten nicht geladen werden');
    }
  };

  // Preisverlauf laden
  const loadPriceHistory = async () => {
    try {
      const response = await api.get(`/products/${productId}/price-history?days=30`);
      setPriceHistory(response.data.history || {});
    } catch (err) {
      console.error('Error loading price history:', err);
    }
  };

  // Initial laden
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadPrices(), loadPriceHistory()]);
      setLoading(false);
    };
    loadData();
  }, [productId]);

  // Manuelle Aktualisierung - nutzt gespeicherte URLs aus product_sources
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      // Neuer Endpoint: Nutzt die gespeicherten URLs fuer dieses Produkt
      const response = await api.post(`/products/${productId}/refresh-prices`);

      if (response.data.success) {
        // Preise wurden aktualisiert - neu laden
        setPrices(response.data.prices || []);
        await loadPriceHistory();
      } else {
        // Keine URLs vorhanden oder Fehler
        setError(response.data.error || response.data.message);
        // Trotzdem die vorhandenen Preise anzeigen
        if (response.data.prices) {
          setPrices(response.data.prices);
        }
      }
    } catch (err) {
      console.error('Refresh error:', err);
      setError('Fehler beim Aktualisieren: ' + (err.response?.data?.error || err.message));
    } finally {
      setRefreshing(false);
    }
  };

  // Chart-Daten vorbereiten
  const prepareChartData = () => {
    const dataMap = new Map();

    Object.entries(priceHistory).forEach(([supplier, entries]) => {
      entries.forEach((entry) => {
        const date = entry.date;
        if (!dataMap.has(date)) {
          dataMap.set(date, { date });
        }
        dataMap.get(date)[supplier] = entry.price;
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Preis formatieren
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return Number(price).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €';
  };

  // Verfügbarkeits-Badge
  const AvailabilityBadge = ({ availability }) => {
    const styles = {
      in_stock: 'bg-green-100 text-green-700',
      limited: 'bg-yellow-100 text-yellow-700',
      out_of_stock: 'bg-red-100 text-red-700',
      unknown: 'bg-gray-100 text-gray-500',
    };
    const labels = {
      in_stock: 'Sofort lieferbar',
      limited: 'Begrenzt verfügbar',
      out_of_stock: 'Nicht verfügbar',
      unknown: 'Keine Angabe',
    };

    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${styles[availability] || styles.unknown}`}>
        {labels[availability] || labels.unknown}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Spinner size="lg" />
        <p className="mt-4 text-gray-500">Lade Preisvergleich...</p>
      </div>
    );
  }

  const chartData = prepareChartData();
  const suppliers = Object.keys(priceHistory);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{productName}</h3>
          {articleNumber && (
            <p className="text-sm text-gray-500 font-mono">Art.Nr: {articleNumber}</p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          loading={refreshing}
          icon={RefreshCw}
        >
          Aktualisieren
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Preisvergleichs-Tabelle */}
      {prices.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lieferant
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preis
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="flex items-center justify-end gap-1">
                    <Percent className="w-3 h-3" />
                    Rueckverg.
                  </span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effektiv
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verfügbar
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktualisiert
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {prices.map((price, idx) => (
                <tr
                  key={idx}
                  className={price.isCheapest ? 'bg-green-50' : 'hover:bg-gray-50'}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {price.supplierName || price.supplier}
                      </span>
                      {price.isCheapest && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          <Check className="w-3 h-3" />
                          Bester Preis
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatPrice(price.price)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {price.rebatePercent ? (
                      <span className="text-green-600 font-medium">
                        -{price.rebatePercent}%
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-mono font-medium ${price.isCheapest ? 'text-green-700' : 'text-gray-900'}`}>
                      {formatPrice(price.effectivePrice)}
                    </span>
                    {price.differenceToMin > 0 && (
                      <span className="block text-xs text-gray-400">
                        +{formatPrice(price.differenceToMin)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <AvailabilityBadge availability={price.availability} />
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500">
                    <div className="flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" />
                      {(price.lastUpdated || price.scrapedAt) ? new Date(price.lastUpdated || price.scrapedAt).toLocaleDateString('de-DE') : '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {price.url && (
                      <a
                        href={price.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-500 hover:text-primary-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card className="p-6 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Noch keine Preise verfügbar</p>
          <p className="text-sm text-gray-400 mt-1">
            Klicken Sie auf "Aktualisieren" um Preise abzurufen
          </p>
        </Card>
      )}

      {/* Preisverlauf-Chart Toggle */}
      {Object.keys(priceHistory).length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <button
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            onClick={() => setShowChart(!showChart)}
          >
            <div className="flex items-center gap-2 text-gray-700">
              <LineChart className="w-5 h-5" />
              <span className="font-medium">Preisverlauf (30 Tage)</span>
            </div>
            {showChart ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showChart && chartData.length > 0 && (
            <div className="p-4">
              <ResponsiveContainer width="100%" height={300}>
                <RechartsLineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getDate()}.${d.getMonth() + 1}`;
                    }}
                    fontSize={12}
                  />
                  <YAxis
                    tickFormatter={(value) => `${value}€`}
                    fontSize={12}
                  />
                  <Tooltip
                    formatter={(value) => [`${formatPrice(value)}`, '']}
                    labelFormatter={(date) => `Datum: ${new Date(date).toLocaleDateString('de-DE')}`}
                  />
                  <Legend />
                  {suppliers.map((supplier) => (
                    <Line
                      key={supplier}
                      type="monotone"
                      dataKey={supplier}
                      stroke={SUPPLIER_COLORS[supplier] || SUPPLIER_COLORS.Default}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Hinweis */}
      <p className="text-xs text-gray-400 text-center">
        Preise werden automatisch jede Nacht um 3:00 Uhr aktualisiert
      </p>
    </div>
  );
};

export default PriceComparison;
